/**
 * Reusable KPI card for a single exposed value.
 * Provide `translationPrefix` for module-specific translations.
 */

import { BarChart3 } from "lucide-react";
import { useLanguage } from "@/core/LanguageContext";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { formatValue } from "@/core/lib/exposedValues";
import type { ExposedValueDefinition, ExposedValueMapping, RealTimeValue } from "@/core/types/exposed-values";

const miniTrendHeights = [6, 12, 8, 14, 10, 16, 11, 18];

interface ExposedValueMetricCardProps {
  translationPrefix: string;
  definition: ExposedValueDefinition;
  mapping: ExposedValueMapping | null;
  realTimeValues: Record<string, RealTimeValue>;
  onSelect: () => void;
  onOpenChart: (mapping: ExposedValueMapping) => void;
  disabled?: boolean;
}

export const ExposedValueMetricCard = ({
  translationPrefix,
  definition,
  mapping,
  realTimeValues,
  onSelect,
  onOpenChart,
  disabled = false,
}: ExposedValueMetricCardProps) => {
  const { t } = useLanguage();
  const value = mapping ? realTimeValues[mapping.field_mapping_id]?.value : null;
  const unitSymbol = mapping?.field_mapping?.datapoint?.unit?.symbol;
  const hasMapping = Boolean(mapping);
  const dailyAverage = null;

  return (
    <Card className="border-border/70">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">{definition.display_name}</CardTitle>
            <CardDescription className="text-xs">
              {definition.description ||
                t(`${translationPrefix}.metric.noDescription`, "No description")}
            </CardDescription>
          </div>
          <Badge variant={hasMapping ? "secondary" : "outline"}>
            {hasMapping
              ? t(`${translationPrefix}.metric.mapped`, "Mapped")
              : t(`${translationPrefix}.metric.unmapped`, "Unmapped")}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end justify-between">
          <div>
            <p className={`text-2xl font-semibold ${hasMapping ? "" : "text-muted-foreground"}`}>
              {formatValue(value)}
              {unitSymbol && <span className="text-xs font-normal ml-1">{unitSymbol}</span>}
            </p>
            <p className="text-xs text-muted-foreground">
              {mapping?.field_mapping?.datapoint?.display_name ??
                t(`${translationPrefix}.metric.noMapping`, "No mapping")}
            </p>
          </div>
          <MiniTrend />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{t(`${translationPrefix}.metric.dailyAverage`, "Daily avg")}</span>
          <span className="font-medium text-foreground">
            {formatValue(dailyAverage)}
            {unitSymbol && <span className="text-[10px] font-normal ml-1">{unitSymbol}</span>}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {hasMapping ? (
            <Button variant="outline" size="sm" onClick={() => mapping && onOpenChart(mapping)}>
              <BarChart3 className="h-3 w-3 mr-2" />
              {t(`${translationPrefix}.metric.viewTrend`, "View trend")}
            </Button>
          ) : (
            <Button variant="outline" size="sm" disabled={disabled} onClick={onSelect}>
              {t(`${translationPrefix}.metric.selectDatapoint`, "Select datapoint")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const MiniTrend = () => (
  <div className="flex items-end gap-1 h-10">
    {miniTrendHeights.map((height, index) => (
      <span
        key={index}
        className="w-1.5 rounded-sm bg-primary/30"
        style={{ height: `${height}px` }}
      />
    ))}
  </div>
);
